import { SlidersHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { HistorySection } from '@/features/audit-log';
import { usePermissions } from '@/features/auth';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatDateTime } from '@/shared/lib/format';

import { useCustomerPoints, useCustomerPointsHistory } from '../api/loyalty.queries';
import type {
  CustomerPointsEntry,
  CustomerPointsHistoryParams,
  PointsDirection,
  PointsKind,
} from '../types/loyalty.types';
import { PointsAdjustmentDialog } from './points-adjustment-dialog';

const PAGE_SIZE = 10;
const ANY = 'any';
const NO_ENTRIES: CustomerPointsEntry[] = [];

const KIND_LABELS: Record<PointsKind, string> = {
  registration: 'Registration',
  purchase: 'Purchase',
  adjustment: 'Manual adjustment',
  legacy: 'Earlier system',
};

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums">{value.toLocaleString()}</dd>
    </div>
  );
}

/**
 * A customer's loyalty points on the customer page (loyalty.read): balance, every movement with
 * its source and reason, and manual adjustments (loyalty.adjust).
 */
export function CustomerPointsSection({
  customerId,
  customerName,
}: {
  customerId: number;
  customerName: string;
}) {
  const { can } = usePermissions();
  const canAdjust = can('loyalty.adjust');
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<PointsKind | typeof ANY>(ANY);
  const [direction, setDirection] = useState<PointsDirection | typeof ANY>(ANY);
  const [adjusting, setAdjusting] = useState(false);

  const params = useMemo<CustomerPointsHistoryParams>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      kind: kind === ANY ? undefined : kind,
      direction: direction === ANY ? undefined : direction,
    }),
    [page, kind, direction],
  );
  const balance = useCustomerPoints(customerId);
  const history = useCustomerPointsHistory(customerId, params);
  const entries = history.data?.items ?? NO_ENTRIES;
  const filtered = kind !== ANY || direction !== ANY;
  const totalPages = history.data?.totalPages ?? 1;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Loyalty points</CardTitle>
        {canAdjust && balance.data && (
          <Button size="sm" variant="outline" onClick={() => setAdjusting(true)}>
            <SlidersHorizontal className="size-3.5" aria-hidden />
            Adjust points
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4 text-xs">
        {balance.isLoading ? (
          <Skeleton className="h-12" />
        ) : balance.error || !balance.data ? (
          <ErrorState error={balance.error} onRetry={() => void balance.refetch()} />
        ) : (
          <dl aria-label="Points balance" className="grid grid-cols-3 gap-4">
            <Stat label="Available" value={balance.data.available} />
            <Stat label="Total earned" value={balance.data.totalEarned} />
            <Stat label="Total deducted" value={balance.data.totalConsumed} />
          </dl>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={kind}
            onValueChange={(value) => {
              setKind(value as PointsKind | typeof ANY);
              setPage(1);
            }}
          >
            <SelectTrigger size="sm" className="h-8 w-44 text-xs" aria-label="Filter by source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Any source
              </SelectItem>
              {(Object.keys(KIND_LABELS) as PointsKind[]).map((k) => (
                <SelectItem key={k} value={k} className="text-xs">
                  {KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={direction}
            onValueChange={(value) => {
              setDirection(value as PointsDirection | typeof ANY);
              setPage(1);
            }}
          >
            <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Filter by type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Earned and deducted
              </SelectItem>
              <SelectItem value="credit" className="text-xs">
                Earned
              </SelectItem>
              <SelectItem value="debit" className="text-xs">
                Deducted
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {history.isLoading ? (
          <Skeleton className="h-24" />
        ) : history.error ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} />
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground">
            {filtered ? 'No points movements match these filters.' : 'No points movements yet.'}
          </p>
        ) : (
          <ul aria-label="Points history" className="divide-y">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {KIND_LABELS[entry.kind]}
                    {entry.orderId !== null && (
                      <>
                        {' · '}
                        <Link
                          to={`/orders/${entry.orderId}`}
                          className="text-primary hover:underline"
                        >
                          Order #{entry.orderId}
                        </Link>
                      </>
                    )}
                  </p>
                  {entry.reason && (
                    <p className="text-muted-foreground break-words">{entry.reason}</p>
                  )}
                  <p className="text-muted-foreground">
                    {formatDateTime(entry.occurredAt)}
                    {entry.staffName && ` · by ${entry.staffName}`}
                    {entry.balanceAfter !== null && ` · balance ${entry.balanceAfter}`}
                  </p>
                </div>
                <StatusBadge variant={entry.direction === 'credit' ? 'success' : 'danger'}>
                  {entry.direction === 'credit' ? `+${entry.points}` : `−${entry.points}`}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <span className="text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}

        <HistorySection
          entityType="CustomerPoints"
          entityId={String(customerId)}
          title="Adjustment audit"
          collapsible
        />
      </CardContent>

      {adjusting && balance.data && (
        <PointsAdjustmentDialog
          customerId={customerId}
          customerName={customerName}
          available={balance.data.available}
          onClose={() => setAdjusting(false)}
        />
      )}
    </Card>
  );
}
